import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PesquisaPreVendas, PreVenda } from "./PesquisaPreVendas";

import { FiltroPesquiaPreVenda } from "app/api/classes/ProdutoApi";
import microuniversoApi from "app/api/microuniversoApi";
import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";
import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container";
import { renderizarComponentReact } from "app/components/renderizarOuRemoverComponentReact";
import { IColuna } from "app/types/IColuna";
import { tratamentoErroPadrao } from "app/use-cases";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { Dict } from "app/utils/Dict";
import globalMU from "globalMU";
import { substituirItemEmArray } from "substituirItemEmArray";
import {
  PaginaPesquisaProdutoContainer
} from "../PaginaPesquisaProdutoContainer";

export type PesquisaPreVendaContainerProps = {
  onVoltar: () => void,
  passos: Dict<string, () => void>
  regiaoRenderizada: string
  orcamentoForaModuloVendas: boolean
};

const _NOME_GRID_PRE_VENDA = "PESQUISAR_PRODUTOS_PREVENDA"

export const PesquisaPreVendaContainer = (props: PesquisaPreVendaContainerProps) => {
  const isMounted = useRef(true)

  const [colunas, setColunas] = useState<IColuna[]>([]);
  const [tamanhoFonte, setTamanhoFonte] = useState<number>(0);
  const [permiteEngrenagemGrid, setPermiteEngrenagemGrid] = useState(false);
  const [dadosPreVenda, setDadosPreVenda] = useState<PagedMessageResponse<PreVenda>>();
  const [filtroPreVenda, setFiltroPreVenda] = useState<FiltroPesquiaPreVenda>({
    Localizar: "",
    NumeroPagina: 1,
    TamanhoPagina: 20,
  });

  // Refs de leitura (item #21) — permitem que os callbacks abaixo leiam o valor mais atual de
  // `colunas`/`filtroPreVenda` sem precisar deles nas deps do useCallback, mantendo identidade
  // estável entre renders (evita invalidar o memo() de PesquisaPreVendas a cada busca/tecla).
  const colunasRef = useRef(colunas)
  colunasRef.current = colunas
  const filtroPreVendaRef = useRef(filtroPreVenda)
  filtroPreVendaRef.current = filtroPreVenda

  const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, props.passos.map)))

  useEffect(() => {
    return () => { isMounted.current = false }
  }, [])

  const obterDadosIniciaisGrid = useCallback(async () => {
    if (!isMounted.current) return
    try {
      globalMU.blockUI();
      const dadosGrid = await microuniversoApi.Seguranca.obterDadosGrid("Orcamento", _NOME_GRID_PRE_VENDA);
      if (!isMounted.current) return
      setColunas(dadosGrid.Colunas);
      setTamanhoFonte(dadosGrid.TamanhoFonte);
    } catch (e) {
      tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  }, []);

  const permitidoAcaoFuncionalidade = useCallback(async (
    siglaFuncionalidade: string,
    acao?: string
  ) => await obterPermissao({ funcionalidade: siglaFuncionalidade, acao }), []);

  const atualizarPermiteEngrenagemGrid = useCallback(async () => {
    if (!isMounted.current) return
    const permitido = await permitidoAcaoFuncionalidade("ORCAMENTO_ACESSO_ENGRENAGEM_GRID")
    if (!isMounted.current) return
    setPermiteEngrenagemGrid(permitido);
  }, [permitidoAcaoFuncionalidade]);

  const pesquisar = useCallback(async () => {
    if (!isMounted.current) return
    try {
      globalMU.blockUI();
      const dados = await microuniversoApi.Produto.pesquisarPreVenda(
        filtroPreVendaRef.current
      );
      if (!isMounted.current) return
      setDadosPreVenda(dados);
    } catch (e) {
      tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  }, []);

  const pesquisaPaginada = useCallback(async (numeroIndexPagina: number) => {
    if (!isMounted.current) return
    try {
      globalMU.blockUI();
      const filtrosAlterado = {
        ...filtroPreVendaRef.current,
        NumeroPagina: numeroIndexPagina,
      };
      setFiltroPreVenda(filtrosAlterado);
      const dados = await microuniversoApi.Produto.pesquisarPreVenda(
        filtrosAlterado
      );
      if (!isMounted.current) return
      setDadosPreVenda(dados);
    } catch (e) {
      tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  }, []);

  const toggleColunaSelecionada = useCallback(async (
    nomeColuna: string,
    selecionada: boolean
  ) => {
    const colunasAtuais = colunasRef.current
    const coluna = { ...colunasAtuais.find((c) => c.Nome == nomeColuna), Selecionada: selecionada };
    const colunas_ = substituirItemEmArray(colunasAtuais, (coluna) => coluna.Nome == nomeColuna,
      coluna
    );
    setColunas(colunas_);
    await microuniversoApi.Seguranca.atualizarColunasGrid(colunas_, _NOME_GRID_PRE_VENDA);
  }, []);

  const toggleSelecionarTodasColunas = useCallback(async (selecionar: boolean) => {
    const colunas_ = colunasRef.current.map(
      (c) => ({ ...c, Selecionada: selecionar } as IColuna)
    );
    setColunas(colunas_);
    await microuniversoApi.Seguranca.atualizarColunasGrid(colunas_, _NOME_GRID_PRE_VENDA);
  }, []);

  const alterarFonte = useCallback(async (tamanho: number) => {
    globalMU.blockUI();
    try {
      await microuniversoApi.Usuario.alterarFonte(_NOME_GRID_PRE_VENDA, tamanho);
    } catch (e) {
      tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  }, []);

  const onAlterarFonte = useCallback((fonte: number) => {
    alterarFonte(fonte);
    setTamanhoFonte(fonte);
  }, [alterarFonte]);

  const onLimparCampoLocalizar = useCallback(async () => {
    if (!isMounted.current) return
    try {
      globalMU.blockUI();
      setFiltroPreVenda(prev => ({ ...prev, Localizar: "" }));
      const dados = await microuniversoApi.Produto.pesquisarPreVenda({
        ...filtroPreVendaRef.current,
        Localizar: "",
      });
      if (!isMounted.current) return
      setDadosPreVenda(dados);
    } catch (e) {
      tratamentoErroPadrao(e);
    } finally {
      globalMU.unblockUI();
    }
  }, []);

  const onAlterarCampoLocalizar = useCallback((localizar: string) => {
    setFiltroPreVenda(prev => ({ ...prev, Localizar: localizar }));
  }, []);

  const onMensagemError = useCallback(() => {
    globalMU.showErrorMessage(
      "Por favor, selecione as pré-vendas a serem adicionadas."
    );
  }, []);

  const onPassoSelecionado = useCallback((passo: string) => {
    passosInterno.current.get(passo)()
  }, []);

  const onVoltar = useCallback(() => {
    props.onVoltar()
  }, [props.onVoltar]);

  const onPreVendasSelecionadas = useCallback((preVendaSelecionada: PreVenda[]) => {
    
    renderizarComponentReact(
      <PaginaPesquisaProdutoContainer
        // Precisa ser true: é o gate que faz o <Dialog id="PaginaPesquisaProduto_DialogCestaProdutos">
        // existir no DOM (ver PaginaPesquisaProduto.tsx) — sem ele, abrirModalCestaProdutosAction
        // (disparado em pesquisaPreVenda ao confirmar a pré-venda) não tem modal para abrir.
        chamadoParaInserirProdutoNoOrcamento={true}
        chamadoPeloTrocarProduto={false}
        // 491123 — precisa ser false: true força `orcamento = {}` em PaginaPesquisaProdutoContainer
        // (ver linha `props.foraOrcamento || props.pesquisandoApenasParaFiltroProdutosLupa ? {} : ...`),
        // e sem orcamento.IdentificadorOrcamento o onAdicionarItensGrid não consegue gravar os itens
        // da pré-venda no servidor (mesmo padrão false usado em ItensOrcamentoContainer.tsx/
        // PesquisaCorContainer.tsx/PesquisaProdutoForaOrcamento.tsx para este mesmo cenário de
        // inserir produto de verdade no orçamento).
        pesquisandoApenasParaFiltroProdutosLupa={false}
        passos={props.passos.remove("Pesquisar Produtos")}
        onVoltar={() => onPassoSelecionado("Itens")}
        foraOrcamento={props.orcamentoForaModuloVendas}
        regiaoRenderizada={props.regiaoRenderizada}
        preVendaSelecionada={preVendaSelecionada}
      />,
      props.regiaoRenderizada,
      true
    );
  }, [props.orcamentoForaModuloVendas, props.passos, props.regiaoRenderizada, onPassoSelecionado]);

  useEffect(() => {
    passosInterno.current.set("Pesquisar Pré-Vendas", () => { })
    obterDadosIniciaisGrid();
    atualizarPermiteEngrenagemGrid();
    pesquisar();
  }, []);

  // sessionStorage não muda durante a vida deste componente — lido uma única vez (item #27/#150).
  const corFundoSelecionado = useMemo(() => globalMU.sessionStorage.getData("UsuarioLogado")?.CorFundo, []);
  const corTextoSelecionado = useMemo(() => globalMU.sessionStorage.getData("UsuarioLogado")?.CorTexto, []);

  return (
    <>
      <Breadcrumb2Container
        passos={passosInterno.current.keysToList()}
        exibirBotaoLimpar={false}
        exibirBotaoVoltar={true}
        id="PesquisaPreVendaContainer"
        onPassoSelecionado={onPassoSelecionado}
        onBotaoVoltarClicado={onVoltar}
      />

      <PesquisaPreVendas
        permitirEngrenagemGrid={permiteEngrenagemGrid}
        onAlterarListaSelecionada={pesquisaPaginada}
        colunas={colunas}
        campoBusca={filtroPreVenda.Localizar}
        onAlterarCampoLocalizar={onAlterarCampoLocalizar}
        onLimparCampoLocalizar={onLimparCampoLocalizar}
        corFundoSelecionado={corFundoSelecionado}
        corTextoSelecionado={corTextoSelecionado}
        preVendas={dadosPreVenda}
        tamanhoFontePadrao={tamanhoFonte}
        onPesquisar={pesquisar}
        onAlterarFonte={onAlterarFonte}
        onToggleColunaSelecionada={toggleColunaSelecionada}
        onToggleSelecionarTodasColunas={toggleSelecionarTodasColunas}
        onMensagemError={onMensagemError}
        onPreVendasSelecionadas={onPreVendasSelecionadas}
      />
    </>
  );
};
